(function (root) {
  'use strict';
  function createImageCache({storage, fetcher, origin, makeURL, maxBytes = 30 * 1024 * 1024, maxItem = 2 * 1024 * 1024, maxEntries = 80, lock = task => task()}) {
    const pending = new Map(), urls = new Map();
    let queue = Promise.resolve();
    const keyFor = raw => {
      try {
        const u = new URL(raw, origin);
        return u.origin === origin && /^\/cdn\/shop\/(files|t\/\d+\/assets)\//.test(u.pathname) &&
          /\.(webp|png|jpe?g|avif)$/i.test(u.pathname) && u.searchParams.has('v') ? u.href : null;
      } catch (_) { return null; }
    };
    async function read(key) {
      const cache = await storage.open('solstice-images-v1');
      const hit = await cache.match(key);
      if (hit) {
        const blob = await hit.blob();
        if (blob.size <= maxItem && /^image\/(webp|png|jpeg|avif)$/.test(blob.type)) return {blob, cache, hit:true};
        await cache.delete(key);
      }
      const controller = new AbortController(), timeout = setTimeout(()=>controller.abort(), 2500);
      let response;
      try { response = await fetcher(key, {credentials:'omit', mode:'cors', signal:controller.signal}); }
      finally { clearTimeout(timeout); }
      if (!response.ok) throw Error('Image unavailable');
      const blob = await response.blob();
      if (!/^image\/(webp|png|jpeg|avif)$/.test(blob.type) || blob.size > maxItem || !blob.size) throw Error('Image outside cache budget');
      return {blob, cache, hit:false};
    }
    async function store(cache, key, blob, hit) {
      const keys = await cache.keys();
      let total = 0;
      const entries = [];
      for (const request of keys) {
        const response = await cache.match(request);
        const size = Number(response?.headers.get('x-solstice-bytes')) || 0;
        if (!size) { await cache.delete(request); continue; }
        if (request.url !== key) { total += size; entries.push(request); }
      }
      while (entries.length && (total + blob.size > maxBytes || entries.length >= maxEntries)) {
        const request = entries.shift(), response = await cache.match(request);
        total -= Number(response?.headers.get('x-solstice-bytes')) || 0;
        await cache.delete(request);
      }
      if (hit) await cache.delete(key);
      await cache.put(key, new Response(blob, {headers:{'Content-Type':blob.type,'x-solstice-bytes':String(blob.size)}}));
    }
    async function image(raw) {
      const key = keyFor(raw);
      if (!key || !storage) return raw;
      if (urls.has(key)) return urls.get(key);
      if (pending.has(key)) return pending.get(key);
      const job = (async () => {
        try {
          const {blob,cache,hit} = await read(key);
          const url = makeURL(blob);
          urls.set(key,url);
          queue = queue.catch(()=>{}).then(()=>lock(()=>store(cache,key,blob,hit)));
          await queue.catch(()=>{});
          return url;
        } catch (_) { return raw; }
      })();
      pending.set(key,job);
      try { return await job; } finally { pending.delete(key); }
    }
    async function peek(raw) {
      const key = keyFor(raw);
      if (!key || !storage) return null;
      try {
        const cache = await storage.open('solstice-images-v1');
        const response = await cache.match(key);
        if (!response) return null;
        const blob = await response.blob();
        if (!blob.size || blob.size > maxItem || !/^image\/(webp|png|jpeg|avif)$/.test(blob.type)) return null;
        if (!urls.has(key)) urls.set(key, makeURL(blob));
        return urls.get(key);
      } catch (_) { return null; }
    }
    return {image, peek, keyFor, settled:()=>queue};
  }
  if (typeof module === 'object' && module.exports) { module.exports = {createImageCache}; return; }
  if (root.SolsticeResourceCache) return;
  const cache = createImageCache({storage:navigator.locks ? root.caches : null,fetcher:root.fetch.bind(root),origin:location.origin,makeURL:b=>URL.createObjectURL(b),lock:task=>navigator.locks.request('solstice-image-cache-v1',task)});
  root.SolsticeResourceCache = cache;
  let restored = 0;
  const seen = new WeakSet();
  const restoreVisible = () => {
    if (document.hidden || restored >= 6 || /(^|\/)checkout|\/account|\/password/.test(location.pathname)) return;
    for (const img of document.querySelectorAll('main img')) {
      if (restored >= 6) break;
      if (seen.has(img) || img.complete || img.getBoundingClientRect().top >= innerHeight * 2) continue;
      seen.add(img); restored++;
      const source = img.currentSrc || img.src;
      cache.peek(source).then(url => {
        if (!url || !img.isConnected || img.complete || (img.currentSrc || img.src) !== source || img.closest('picture')) return;
        if (img.srcset) {
          img.srcset = img.srcset.split(',').map(candidate => {
            const parts = candidate.trim().split(/\s+/);
            try { if (new URL(parts[0], location.href).href === source) parts[0] = url; } catch (_) {}
            return parts.join(' ');
          }).join(', ');
        }
        if (!img.srcset || img.src === source) img.src = url;
      });
    }
  };
  const observer = new MutationObserver(restoreVisible);
  observer.observe(document, {childList:true, subtree:true});
  root.addEventListener('load',()=>observer.disconnect(),{once:true});
  const warmVisible = () => {
    if (document.hidden || navigator.connection?.saveData || /(^|\/)checkout|\/account|\/password/.test(location.pathname)) return;
    const candidates = [...document.querySelectorAll('main img')].filter(i=>i.complete && i.naturalWidth && i.getBoundingClientRect().top < innerHeight * 2).slice(0,6);
    for (const image of candidates) cache.image(image.currentSrc || image.src).catch(()=>{});
  };
  root.addEventListener('load',()=>{ (root.requestIdleCallback || (f=>setTimeout(f,500)))(warmVisible); },{once:true});
})(typeof window === 'object' ? window : globalThis);
